/**
 * Formatting helpers for task rows.
 *
 * Split out of `TaskList.tsx` so that file exports components only — a module exporting
 * both components and plain functions breaks React Fast Refresh.
 */

import { describeRecurrence, parseRecurrence } from "./recurrence";

/**
 * The label a screen reader and a tooltip get for activation cost (§8.3).
 *
 * The visual form is `<Die />` — drawn, not typed, so the frame and pips can differ in
 * colour. This is the only textual description of it, deliberately in one place.
 */
export function energyTitle(energy: number | null): string {
  return energy === null
    ? "No activation cost set - always shown, whatever the slider says"
    : `Activation cost ${energy} of 5`;
}

/** Calendar-date comparison, which works because §5.2 stores dates as `YYYY-MM-DD` TEXT. */
export function isOverdue(due: string | null, todayDate: string): boolean {
  return due !== null && due < todayDate;
}

/**
 * Days from `todayDate` to `due`, both `YYYY-MM-DD`. Positive is in the future.
 *
 * Parsed into UTC components rather than handed to `new Date(str)` — both strings are
 * already plain calendar dates (§5.2), so this only ever needs calendar-day arithmetic, and
 * pinning it to UTC sidesteps the DST-shifted-by-a-day bug that bit an earlier local-`Date`
 * attempt at exactly this kind of diff.
 */
function daysFromToday(due: string, todayDate: string): number {
  const [ty, tm, td] = todayDate.split("-").map(Number);
  const [dy, dm, dd] = due.split("-").map(Number);
  const from = Date.UTC(ty, tm - 1, td);
  const to = Date.UTC(dy, dm - 1, dd);
  return Math.round((to - from) / 86_400_000);
}

/**
 * How a due date reads relative to today: `Yesterday`, `Today`, `Tomorrow`, `In 3 days`,
 * `4 days ago` — and, past `capDays` in either direction, the plain date, since "In 40 days"
 * carries less than the date itself would.
 */
export function relativeDateLabel(due: string, todayDate: string, capDays = 6): string {
  const diff = daysFromToday(due, todayDate);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  if (diff > 1 && diff <= capDays) return `In ${diff} days`;
  if (diff < -1 && diff >= -capDays) return `${-diff} days ago`;
  return due;
}

/**
 * How a recurring task's rule reads on its card, e.g. "every 3 months" (§4.6.2).
 *
 * Worth showing at all because a recurring task is a different kind of thing from a one-off
 * — its own occurrence is completed from the Recurring card, not this row — and the badge
 * is a plain-English reminder of the rule, never the RRULE string itself.
 */
export function recurrenceLabel(rrule: string): string {
  const values = parseRecurrence(rrule, null);
  const sentence = describeRecurrence(values).replace(/, forever$/, "");
  return sentence.charAt(0).toLowerCase() + sentence.slice(1);
}

/**
 * How long a task is expected to take, e.g. `5m`, `1h 30m`, `1d 4h`.
 *
 * Largest unit first, zero parts omitted, so `120` is `2h` rather than `2h 0m`. A day is 24
 * hours here and not a working day: this is a duration, not a schedule, and inventing an
 * eight-hour day would make `1d` mean something the number never said.
 */
export function estimateLabel(minutes: number): string {
  if (minutes < 1) return "0m";

  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = Math.round(minutes % 60);

  const parts = [
    days > 0 ? `${days}d` : "",
    hours > 0 ? `${hours}h` : "",
    mins > 0 ? `${mins}m` : "",
  ].filter(Boolean);

  return parts.join(" ");
}

/**
 * When a capture actually happened, for the badge on an inbox row (§8.1).
 *
 * `capturedAt` is when the *client* recorded the thought — a capture spoken underground and
 * flushed hours later has a `created_at` four hours late, and only the client can say which
 * is which. Falls back to `createdAt` for a source that never sent one: the CLI, or a
 * request with no idempotency-minting client behind it, and "when it arrived" is still an
 * honest answer when "when it was said" was never available.
 *
 * Time-only when the capture was today; otherwise a short date is prepended, since "9:30 AM"
 * from three days ago reads as this morning.
 */
export function captureTimeLabel(createdAt: number, capturedAt: string | null | undefined): string {
  const when = capturedAt ? new Date(capturedAt) : new Date(createdAt * 1000);
  const time = when.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  if (when.toDateString() === new Date().toDateString()) return time;
  return `${when.toLocaleDateString(undefined, { month: "short", day: "numeric" })}, ${time}`;
}

/**
 * A `geo:` URI for a capture's location (§8.1).
 *
 * Not a `google.com/maps` link. `geo:` is the platform-standard intent and opens whatever map
 * app the phone actually has set as its handler — Google Maps or otherwise. A literal Google
 * Maps URL would work for the one reader who has that set as default and be a worse
 * experience for everyone else, on the platform this badge is mainly for.
 */
export function mapsHref(lat: number, lon: number): string {
  return `geo:${lat},${lon}?q=${lat},${lon}`;
}
